import React, { useState, useEffect } from 'react';

const WizardStepTargetWeight = ({ onNext, onBack, setWizardData, wizardData }) => {
  const [selectedWeight, setSelectedWeight] = useState(null);

  // Initialize selected weight from wizard data
  useEffect(() => {
    if (wizardData.target_weight && wizardData.weight_type) {
      const weightOptions = getWeightOptions();
      const existingOption = weightOptions.find(
        option => option.amount === wizardData.target_weight && option.unit === wizardData.weight_type
      );
      if (existingOption) {
        setSelectedWeight(existingOption);
      }
    }
  }, [wizardData.metal_type]);

  // Define target weight options for each metal type
  const getWeightOptions = () => {
    switch(wizardData.metal_type) {
      case 'gold':
        return [
          { amount: 1.0, unit: 'g', display: '1 Gram', icon: '🟡', description: 'Perfect starter amount' },
          { amount: 2.5, unit: 'g', display: '2.5 Grams', icon: '🟡', description: 'Small investment goal' },
          { amount: 5.0, unit: 'g', display: '5 Grams', icon: '🟡', description: 'Popular choice' },
          { amount: 10.0, unit: 'g', display: '10 Grams', icon: '🟡', description: 'Serious investment' },
          { amount: 1.0, unit: 'oz', display: '1 Troy Ounce', icon: '🥇', description: 'Traditional standard' }
        ];
      case 'silver':
        return [
          { amount: 1.0, unit: 'oz', display: '1 Troy Ounce', icon: '🥈', description: 'Classic silver coin' },
          { amount: 2.5, unit: 'oz', display: '2.5 Troy Ounces', icon: '🥈', description: 'Small stack' },
          { amount: 5.0, unit: 'oz', display: '5 Troy Ounces', icon: '🥈', description: 'Popular choice' },
          { amount: 10.0, unit: 'oz', display: '10 Troy Ounces', icon: '🥈', description: 'Solid investment' }
        ];
      case 'platinum':
        return [
          { amount: 1.0, unit: 'g', display: '1 Gram', icon: '⚪', description: 'Starter amount' },
          { amount: 2.5, unit: 'g', display: '2.5 Grams', icon: '⚪', description: 'Small investment' },
          { amount: 5.0, unit: 'g', display: '5 Grams', icon: '⚪', description: 'Popular choice' },
          { amount: 10.0, unit: 'g', display: '10 Grams', icon: '⚪', description: 'Premium investment' }
        ];
      default:
        return [];
    }
  };

  const handleWeightSelect = (weightOption) => {
    setSelectedWeight(weightOption);
    setWizardData({
      ...wizardData,
      target_weight: weightOption.amount,
      weight_type: weightOption.unit
    });
  };

  const handleNext = () => {
    if (selectedWeight) {
      onNext();
    }
  };

  const isSelected = (option) => {
    return selectedWeight &&
           selectedWeight.amount === option.amount &&
           selectedWeight.unit === option.unit;
  };

  const weightOptions = getWeightOptions();

  return (
    <div className="min-h-[450px] bg-gradient-to-br from-gray-50 to-gray-200 py-3 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Step Description */}
        <h2 className="text-center text-2xl font-bold text-gray-800 mb-2">Select your target weight</h2>
        <p className="text-center text-gray-600 mb-4">This is the amount of {wizardData.metal_type} you'll receive once you hit your goal.</p>

        {/* Content Section */}
        <div className="flex justify-center">
          <div className="w-full">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {weightOptions.map((option, index) => (
                <div
                  key={index}
                  onClick={() => handleWeightSelect(option)}
                  className={`
                    relative bg-white rounded-2xl p-5 cursor-pointer
                    transition-all duration-300 ease-in-out
                    border-2 shadow-md min-h-[240px]
                    hover:transform hover:-translate-y-1 hover:shadow-xl
                    ${isSelected(option)
                      ? 'border-blue-500 shadow-blue-200 transform -translate-y-1'
                      : 'border-gray-200 hover:border-blue-500'
                    }
                  `}
                >
                  {isSelected(option) && (
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-500/5 rounded-2xl"></div>
                  )}

                  <div className="relative z-10 flex flex-col items-center text-center h-full">
                    <div className="text-4xl mb-2 opacity-80 transition-opacity duration-300 hover:opacity-100">
                      {option.icon}
                    </div>

                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      {option.display}
                    </h3>

                    <p className="text-sm text-gray-600 leading-snug flex-grow">
                      {option.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Navigation Section */}
        <div className="flex justify-between mt-4">
          <button
            onClick={onBack}
            className="bg-white text-black px-6 py-2 rounded-full font-bold border-2 border-black hover:bg-gray-50 transition-colors"
          >
            <i className="bi bi-arrow-left mr-2"></i>
            Back
          </button>
          <button
            onClick={handleNext}
            disabled={!selectedWeight}
            className={`px-6 py-2 rounded-full font-bold transition-colors ${
              selectedWeight
                ? 'bg-black text-white hover:bg-gray-800'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Continue
            <i className="bi bi-arrow-right ml-2"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default WizardStepTargetWeight;
